import { useMemo, useState, useRef, useEffect } from 'react'
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom'
import {
  LayoutDashboard, Library, Search,
  BookMarked, LogOut, ChevronLeft, ChevronDown, ChevronRight, Menu,
  ChevronUp, Settings, User, BarChart2,
  Sun, Moon, Zap, Star,
} from 'lucide-react'
import { useAuthStore, selectUser } from '../stores/authStore'
import { logoutApi } from '../api/client'
import { useDecks } from '../hooks/useDecks'
import { useProgressiveNav } from '../hooks/useProgressiveNav'
import { useProgressSummary } from '../hooks/useProgress'
import { useSwipeNavBlock } from '../hooks/useSwipeNavBlock'
import { onChainNavClick } from '../utils/chainNav'
import { useLocale } from '../i18n/context'
import { BOTTOM_NAV_HEIGHT, isBottomNavRoute } from '../utils/bottomNav'
import { useLookupPaletteStore } from '../stores/lookupPaletteStore'
import ThemeSwitch from './shared/ThemeToggle'

// Injected once; drives the fade-in when a new section mounts
const FADE_IN_STYLE = `
  @keyframes sidebarFadeIn {
    from { opacity: 0; transform: translateY(-6px); }
    to   { opacity: 1; transform: translateY(0); }
  }
`

/** Wraps a conditional sidebar section so it fades in on first render */
function AnimatedSection({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ animation: 'sidebarFadeIn 0.2s ease both' }}>
      {children}
    </div>
  )
}

interface Props {
  collapsed: boolean
  onToggleCollapse: () => void
  theme: 'light' | 'dark'
  onSetTheme: (theme: 'light' | 'dark') => void
}

export default function Sidebar({ collapsed, onToggleCollapse, theme, onSetTheme }: Props) {
  const user    = useAuthStore(selectUser)
  const navigate = useNavigate()
  const { tr }  = useLocale()
  const [userMenuOpen, setUserMenuOpen] = useState(false)
  const userMenuRef = useRef<HTMLDivElement>(null)
  const openLookup = useLookupPaletteStore((s) => s.openAsModal)

  // Close popup when clicking outside
  useEffect(() => {
    if (!userMenuOpen) return
    const handler = (e: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setUserMenuOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [userMenuOpen])

  const { data: decksData } = useDecks(1, 100)

  // Total due count across all decks for the badge on Home
  const totalDue = (decksData?.items ?? []).reduce((sum, d) => sum + (d.due_count ?? 0), 0)

  // ── Progressive disclosure tiers ──────────────────────────────────────────
  const deckTotal = decksData?.total ?? 0
  const hasStudied = (user?.words_known_count ?? 0) > 0
  const isEngaged = deckTotal >= 3 || hasStudied

  const handleLogout = async () => {
    setUserMenuOpen(false)
    await logoutApi()
    navigate('/login', { replace: true })
  }

  return (
    <aside
      className="hidden md:flex flex-col"
      style={{
        width: collapsed ? 64 : 260,
        flexShrink: 0,
        backgroundColor: 'var(--bg-surface)',
        borderRight: '1px solid var(--border)',
        transition: 'width 0.2s ease',
        overflow: 'hidden',
        zIndex: 20,
        height: '100vh',
        position: 'sticky',
        top: 0,
      }}
    >
      {/* Inject animation keyframes */}
      <style>{FADE_IN_STYLE}</style>

      {/* Brand + collapse toggle */}
      <div
        style={{
          height: 64,
          display: 'flex',
          alignItems: 'center',
          padding: collapsed ? '0 12px' : '0 16px',
          borderBottom: '1px solid var(--border)',
          gap: 10,
          flexShrink: 0,
        }}
      >
        {!collapsed && (
          <Link to="/home" style={{ textDecoration: 'none', flex: 1 }}>
            <span style={{ fontWeight: 700, fontSize: 17, color: 'var(--text-primary)', letterSpacing: '-0.02em' }}>
              CardSmith
            </span>
          </Link>
        )}
        <button
          onClick={onToggleCollapse}
          title={collapsed ? tr.nav.expandSidebar : tr.nav.collapseSidebar}
          style={iconBtnStyle}
        >
          {collapsed ? <Menu size={18} /> : <ChevronLeft size={18} />}
        </button>
      </div>

      {/* Navigation */}
      <nav style={{ flex: 1, minHeight: 0, padding: '12px 8px', display: 'flex', flexDirection: 'column', gap: 2, overflowY: 'auto' }}>

        {/* Quick lookup — opens the Ctrl/Cmd+K overlay, previously reachable
            only by that shortcut. A plain button, not a NavLink: it opens an
            overlay rather than navigating, so it must never read as "active"
            the way a nav item does for its own route. */}
        <button
          onClick={openLookup}
          title={collapsed ? tr.nav.quickLookup : undefined}
          style={{
            display: 'flex', alignItems: 'center', gap: 10,
            padding: collapsed ? '10px 12px' : '10px 14px',
            borderRadius: 8, border: 'none', background: 'none',
            color: 'var(--text-secondary)', fontSize: 14, fontFamily: 'var(--font-ui)',
            cursor: 'pointer', textAlign: 'left',
            justifyContent: collapsed ? 'center' : 'flex-start',
            minHeight: 44, marginBottom: 4,
          }}
          onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--accent-soft)'; e.currentTarget.style.color = 'var(--text-primary)' }}
          onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = 'var(--text-secondary)' }}
        >
          <Search size={18} />
          {!collapsed && <span style={{ flex: 1 }}>{tr.nav.quickLookup}</span>}
        </button>
        <div style={{ margin: '2px 8px 8px', borderTop: '1px solid var(--border)' }} />

        {/* ── Tier 0 (always visible) ──────────────────────────────────── */}

        {/* Home with due-count badge */}
        <NavItem to="/home" icon={<LayoutDashboard size={18} />} label={tr.nav.home} collapsed={collapsed}>
          {!collapsed && totalDue > 0 && (
            <span style={dueBadgeStyle}>{totalDue > 99 ? '99+' : totalDue}</span>
          )}
        </NavItem>

        {/* Library — unified hub for Mine / Catalog / Explore */}
        <NavItem to="/library" icon={<Library size={18} />} label={tr.nav.library} collapsed={collapsed} />

        {/* ── Tier 2: engaged (3+ decks or has studied) ───────────────── */}
        {isEngaged && (
          <AnimatedSection>
            <NavItem to="/progress" icon={<BarChart2 size={18} />} label={tr.nav.progress} collapsed={collapsed} />
          </AnimatedSection>
        )}

        {/* ── Tier 3: Grammar ─────────────────────────────────────────── */}
        <AnimatedSection>
          <div style={{ margin: '8px 8px', borderTop: '1px solid var(--border)' }} />
          <NavItem to="/grammar" icon={<BookMarked size={18} />} label={tr.nav.grammar} collapsed={collapsed} />
        </AnimatedSection>
      </nav>

      {/* ── User section: avatar triggers popup with Settings + Sign out ── */}
      <div
        ref={userMenuRef}
        style={{ borderTop: '1px solid var(--border)', flexShrink: 0, padding: '8px', position: 'relative' }}
      >
        {/* Popup menu — anchored above the trigger */}
        {userMenuOpen && (
          <div
            style={{
              position: 'absolute',
              bottom: 'calc(100% + 4px)',
              left: 8,
              right: 8,
              backgroundColor: 'var(--bg-surface)',
              border: '1px solid var(--border)',
              borderRadius: 10,
              overflow: 'hidden',
              boxShadow: 'var(--shadow-float)',
              zIndex: 100,
            }}
          >
            <NavLink
              to="/settings"
              onClick={() => setUserMenuOpen(false)}
              style={({ isActive }) => ({
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '11px 14px',
                textDecoration: 'none',
                fontSize: 14,
                fontWeight: isActive ? 600 : 400,
                color: isActive ? 'var(--accent)' : 'var(--text-primary)',
                backgroundColor: isActive ? 'var(--accent-soft)' : 'transparent',
                borderBottom: '1px solid var(--border)',
              })}
              onMouseEnter={(e) => { if (!e.currentTarget.getAttribute('aria-current')) { e.currentTarget.style.backgroundColor = 'var(--accent-soft)' } }}
              onMouseLeave={(e) => { if (!e.currentTarget.getAttribute('aria-current')) { e.currentTarget.style.backgroundColor = 'transparent' } }}
            >
              <Settings size={16} />
              {tr.nav.settings}
            </NavLink>
            <button
              onClick={handleLogout}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '11px 14px',
                width: '100%',
                border: 'none',
                backgroundColor: 'transparent',
                color: 'var(--danger)',
                fontSize: 14,
                cursor: 'pointer',
                fontFamily: 'var(--font-ui)',
                textAlign: 'left',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--danger-soft)' }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent' }}
            >
              <LogOut size={16} />
              {tr.nav.signOut}
            </button>
          </div>
        )}

        {/* Theme switch — a two-state control above the user row, showing
            both states rather than a button whose meaning depends on the
            current theme (moved out of the desktop header bar). Nothing
            above the user row moves for this; it's new content within this
            existing footer block, not a change to the nav/brand above it. */}
        <div style={{ padding: collapsed ? '0 0 8px' : '0 2px 8px', display: 'flex', justifyContent: 'center' }}>
          <ThemeSwitch theme={theme} onSetTheme={onSetTheme} collapsed={collapsed} />
        </div>

        {/* User info trigger */}
        <button
          onClick={() => setUserMenuOpen((o) => !o)}
          title={collapsed ? (user?.display_name || user?.email || 'Account') : 'Account menu'}
          style={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: collapsed ? '9px 0' : '9px 10px',
            borderRadius: 8,
            border: 'none',
            cursor: 'pointer',
            backgroundColor: userMenuOpen ? 'var(--accent-soft)' : 'transparent',
            color: userMenuOpen ? 'var(--accent)' : 'var(--text-secondary)',
            justifyContent: collapsed ? 'center' : 'flex-start',
            fontFamily: 'var(--font-ui)',
            transition: 'background-color 0.15s, color 0.15s',
            minHeight: 40,
          }}
          onMouseEnter={(e) => { if (!userMenuOpen) { e.currentTarget.style.backgroundColor = 'var(--accent-soft)'; e.currentTarget.style.color = 'var(--text-primary)' } }}
          onMouseLeave={(e) => { if (!userMenuOpen) { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = 'var(--text-secondary)' } }}
        >
          {/* Avatar */}
          <div style={{
            width: 30, height: 30, borderRadius: '50%',
            backgroundColor: 'var(--accent-soft)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: 'var(--accent)', fontWeight: 700, fontSize: 13, flexShrink: 0,
          }}>
            {(user?.display_name || user?.email || '?')[0].toUpperCase()}
          </div>

          {!collapsed && user && (
            <>
              <div style={{ minWidth: 0, flex: 1, textAlign: 'left' }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {user.display_name || user.email}
                </div>
                <div style={{ fontSize: 11, color: 'var(--text-secondary)', textTransform: 'capitalize' }}>
                  {user.plan}
                </div>
              </div>
              <ChevronUp
                size={14}
                style={{
                  flexShrink: 0,
                  transform: userMenuOpen ? 'rotate(0deg)' : 'rotate(180deg)',
                  transition: 'transform 0.15s',
                  color: 'var(--text-secondary)',
                }}
              />
            </>
          )}
        </button>
      </div>
    </aside>
  )
}

// ── Mobile bottom nav ──────────────────────────────────────────────────────────

interface BottomNavProps {
  theme?: 'light' | 'dark'
  onToggleTheme?: () => void
}

// Ring geometry constants — defined once outside the component so they don't
// recalculate on every render.
const RING_RADIUS = 21       // px, SVG circle radius
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS  // ≈ 131.9 px

export function BottomNav({ theme, onToggleTheme }: BottomNavProps = {}) {
  const location = useLocation()
  const { pathname } = location
  const navigate = useNavigate()
  const { totalDue } = useProgressiveNav()
  const user = useAuthStore(selectUser)
  const { tr } = useLocale()
  const [profileMenuOpen, setProfileMenuOpen] = useState(false)
  const profileMenuRef = useRef<HTMLDivElement>(null)
  // ADR-106 P3: no top-level swipe while the profile popup is open.
  useSwipeNavBlock(profileMenuOpen, 'profile-popup')

  // Progress ring data — staleTime 30 s; refetches automatically on remount
  // after returning from a session (sessions take >30 s in practice).
  const { data: progress } = useProgressSummary()

  // Prefers-reduced-motion: skip stroke animation for accessibility.
  const prefersReducedMotion = useMemo(
    () => typeof window !== 'undefined'
      ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
      : false,
    [],
  )

  // Ring fill fraction — capped at 1.0 so the ring never wraps.
  const goal = progress?.daily_card_goal ?? 40
  const done = progress?.today_resolutions ?? 0
  const fraction = Math.min(1, goal > 0 ? done / goal : 0)
  // strokeDashoffset: 0 = full ring, CIRCUMFERENCE = empty ring (goal not started)
  const ringOffset = RING_CIRCUMFERENCE * (1 - fraction)

  // Close profile popup when clicking outside
  useEffect(() => {
    if (!profileMenuOpen) return
    const handler = (e: MouseEvent) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(e.target as Node)) {
        setProfileMenuOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [profileMenuOpen])

  const handleLogout = async () => {
    setProfileMenuOpen(false)
    await logoutApi()
    navigate('/login', { replace: true })
  }

  // Hide on the routes that never carry the nav (learn sessions, onboarding,
  // the deck screen — see `isBottomNavRoute`). That is now the whole rule.
  //
  // Two other reasons used to sit beside it and neither could ever have fired:
  // the deck-feed composer's focus, gone with the composer (ADR-067 Decision
  // 5), and the mobile full-screen card editor being open, gone with that
  // editor (Decision F). Both were about the deck screen, which is already a
  // non-nav route — so the nav was hidden there before either condition was
  // consulted.
  const hidden = !isBottomNavRoute(pathname)

  if (hidden) return null

  // ── Tab helper (shared style) ─────────────────────────────────────────────
  const tabStyle = (isActive: boolean): React.CSSProperties => ({
    flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center',
    justifyContent: 'center', gap: 3, padding: '6px 0',
    color: isActive ? 'var(--accent)' : 'var(--text-secondary)',
    textDecoration: 'none', fontSize: 11, fontWeight: 500,
    minHeight: 44, position: 'relative',
    borderTop: isActive ? '2px solid var(--accent)' : '2px solid transparent',
    transition: 'color 0.15s, border-color 0.15s',
  })

  return (
    <nav
      className="flex md:hidden"
      aria-label="Mobile navigation"
      style={{
        position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 20,
        backgroundColor: 'var(--bg-surface)',
        borderTop: '1px solid var(--border)',
        alignItems: 'stretch',
        height: BOTTOM_NAV_HEIGHT,
        paddingBottom: 'env(safe-area-inset-bottom)',
        overflow: 'visible',  // allow center ring to protrude above nav border
      }}
    >
      {/* ── Home ── */}
      {/* Each chain item: already on its stop → scroll to top only (ADR-106 Q12). */}
      <NavLink to="/home" end onClick={onChainNavClick('/home', location)} style={({ isActive }) => tabStyle(isActive)}>
        {({ isActive }) => (
          <>
            <span style={{ color: isActive ? 'var(--accent)' : 'var(--text-secondary)', lineHeight: 1 }}>
              <LayoutDashboard size={22} />
            </span>
            <span>{tr.nav.home}</span>
            {totalDue > 0 && (
              <span style={{ ...dueBadgeStyle, position: 'absolute', top: 4, left: '50%', transform: 'translateX(4px)' }}>
                {totalDue > 9 ? '9+' : totalDue}
              </span>
            )}
          </>
        )}
      </NavLink>

      {/* ── Library ── */}
      <NavLink to="/library" onClick={onChainNavClick('/library', location)} style={({ isActive }) => tabStyle(isActive)}>
        {({ isActive }) => (
          <>
            <span style={{ color: isActive ? 'var(--accent)' : 'var(--text-secondary)', lineHeight: 1 }}>
              <Library size={22} />
            </span>
            <span>{tr.nav.library}</span>
          </>
        )}
      </NavLink>

      {/* ── Center Learn button with daily-progress ring ── */}
      <div
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          overflow: 'visible',
        }}
      >
        {/* Wrapper shifts the button+label up so the ring protrudes above the nav */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2,
            transform: 'translateY(-8px)',
          }}
        >
          <button
            onClick={() => navigate('/learn')}
            aria-label="Start or continue daily learning"
            title={`${done} / ${goal} cards today`}
            style={{
              position: 'relative',
              width: 52,
              height: 52,
              background: 'none',
              border: 'none',
              padding: 0,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minWidth: 44,  // guaranteed touch target
              minHeight: 44,
            }}
          >
            {/* Progress ring SVG — decorative, hidden from AT (the button has aria-label) */}
            <svg
              width="52"
              height="52"
              viewBox="0 0 52 52"
              aria-hidden
              style={{ position: 'absolute', inset: 0 }}
            >
              {/* Background track */}
              <circle
                cx="26" cy="26" r={RING_RADIUS}
                fill="none"
                stroke="var(--border)"
                strokeWidth="3"
              />
              {/* Progress arc — starts at 12 o'clock, rotated -90° */}
              <circle
                cx="26" cy="26" r={RING_RADIUS}
                fill="none"
                stroke={fraction >= 1 ? 'var(--success, var(--accent))' : 'var(--accent)'}
                strokeWidth="3"
                strokeLinecap="round"
                strokeDasharray={RING_CIRCUMFERENCE}
                strokeDashoffset={ringOffset}
                transform="rotate(-90 26 26)"
                style={{
                  transition: prefersReducedMotion ? 'none' : 'stroke-dashoffset 0.5s ease',
                }}
              />
            </svg>

            {/* Inner filled circle — the visible "button face" */}
            <div
              style={{
                width: 40,
                height: 40,
                borderRadius: '50%',
                backgroundColor: 'var(--accent-fill)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'var(--text-on-accent)',
                boxShadow: '0 2px 8px rgba(0,0,0,0.20)',
                position: 'relative',
                zIndex: 1,
              }}
            >
              <Zap size={18} fill="currentColor" strokeWidth={0} />
            </div>
          </button>

          <span style={{ fontSize: 10, fontWeight: 500, color: 'var(--text-secondary)', lineHeight: 1 }}>
            {tr.nav.learn}
          </span>
        </div>
      </div>

      {/* ── Progress ── */}
      <NavLink to="/progress" onClick={onChainNavClick('/progress', location)} style={({ isActive }) => tabStyle(isActive)}>
        {({ isActive }) => (
          <>
            <span style={{ color: isActive ? 'var(--accent)' : 'var(--text-secondary)', lineHeight: 1 }}>
              <BarChart2 size={22} />
            </span>
            <span>{tr.nav.progress}</span>
          </>
        )}
      </NavLink>

      {/* ── Profile tab (always last) — popup with Settings + Sign out ── */}
      <div
        ref={profileMenuRef}
        style={{ flex: 1, position: 'relative', display: 'flex' }}
      >
        {/* Popup — anchored above the tab */}
        {profileMenuOpen && (
          <div
            style={{
              position: 'absolute',
              bottom: '100%',
              right: 0,
              width: 180,
              backgroundColor: 'var(--bg-surface)',
              border: '1px solid var(--border)',
              borderRadius: 10,
              overflow: 'hidden',
              boxShadow: 'var(--shadow-float)',
              zIndex: 100,
              marginBottom: 4,
            }}
          >
            {/* User info header */}
            {user && (
              <div style={{
                padding: '10px 14px',
                borderBottom: '1px solid var(--border)',
              }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {user.display_name || user.email}
                </div>
                <div style={{ fontSize: 11, color: 'var(--text-secondary)', textTransform: 'capitalize' }}>
                  {user.plan}
                </div>
              </div>
            )}
            <NavLink
              to="/settings"
              onClick={() => setProfileMenuOpen(false)}
              style={({ isActive }) => ({
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '11px 14px',
                textDecoration: 'none',
                fontSize: 14,
                fontWeight: isActive ? 600 : 400,
                color: isActive ? 'var(--accent)' : 'var(--text-primary)',
                backgroundColor: isActive ? 'var(--accent-soft)' : 'transparent',
                borderBottom: '1px solid var(--border)',
              })}
              onMouseEnter={(e) => { if (!e.currentTarget.getAttribute('aria-current')) { e.currentTarget.style.backgroundColor = 'var(--accent-soft)' } }}
              onMouseLeave={(e) => { if (!e.currentTarget.getAttribute('aria-current')) { e.currentTarget.style.backgroundColor = 'transparent' } }}
            >
              <Settings size={16} />
              {tr.nav.settings}
            </NavLink>

            {/* Theme toggle */}
            {theme && onToggleTheme && (
              <button
                onClick={onToggleTheme}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '11px 14px', width: '100%',
                  border: 'none', borderBottom: '1px solid var(--border)',
                  backgroundColor: 'transparent',
                  color: 'var(--text-primary)',
                  fontSize: 14, cursor: 'pointer',
                  fontFamily: 'var(--font-ui)', textAlign: 'left',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--accent-soft)' }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent' }}
              >
                {theme === 'dark' ? <Sun size={16} /> : <Moon size={16} />}
                {theme === 'dark' ? tr.nav.lightMode : tr.nav.darkMode}
              </button>
            )}

            {/* Upgrade to Pro — free users only */}
            {user?.plan === 'free' && (
              <NavLink
                to="/pro"
                onClick={() => setProfileMenuOpen(false)}
                style={({ isActive }) => ({
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '11px 14px', textDecoration: 'none',
                  fontSize: 14, fontWeight: 600,
                  color: 'var(--accent)',
                  backgroundColor: isActive ? 'var(--accent-soft)' : 'transparent',
                  borderBottom: '1px solid var(--border)',
                })}
                onMouseEnter={(e) => { if (!e.currentTarget.getAttribute('aria-current')) { e.currentTarget.style.backgroundColor = 'var(--accent-soft)' } }}
                onMouseLeave={(e) => { if (!e.currentTarget.getAttribute('aria-current')) { e.currentTarget.style.backgroundColor = 'transparent' } }}
              >
                <Star size={16} />
                {tr.nav.upgradePro}
              </NavLink>
            )}

            <button
              onClick={handleLogout}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '11px 14px',
                width: '100%',
                border: 'none',
                backgroundColor: 'transparent',
                color: 'var(--danger)',
                fontSize: 14,
                cursor: 'pointer',
                fontFamily: 'var(--font-ui)',
                textAlign: 'left',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--danger-soft)' }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent' }}
            >
              <LogOut size={16} />
              {tr.nav.signOut}
            </button>
          </div>
        )}

        {/* Profile button */}
        <button
          onClick={() => setProfileMenuOpen((o) => !o)}
          aria-label="Account menu"
          style={{
            flex: 1,
            display: 'flex', flexDirection: 'column', alignItems: 'center',
            justifyContent: 'center', gap: 3, padding: '6px 0',
            color: profileMenuOpen ? 'var(--accent)' : 'var(--text-secondary)',
            background: 'none', border: 'none',
            fontSize: 11, fontWeight: 500, minHeight: 44, cursor: 'pointer',
            borderTop: profileMenuOpen ? '2px solid var(--accent)' : '2px solid transparent',
            transition: 'color 0.15s, border-color 0.15s',
          }}
        >
          <User size={22} />
          <span>{tr.nav.profile}</span>
        </button>
      </div>
    </nav>
  )
}

// ── Shared sub-components ──────────────────────────────────────────────────────

// ── Nav item ──────────────────────────────────────────────────────────────────

interface NavItemProps {
  to: string
  icon: React.ReactNode
  label: string
  collapsed?: boolean
  pro?: boolean
  children?: React.ReactNode
  onExpandToggle?: () => void
  expanded?: boolean
}

function NavItem({ to, icon, label, collapsed, pro, children, onExpandToggle, expanded }: NavItemProps) {
  const location = useLocation()
  return (
    <NavLink
      to={to}
      end={to === '/home'}
      // Already on this item's stop: scroll it to the top, nothing else (ADR-106 Q12).
      onClick={onChainNavClick(to, location)}
      title={collapsed ? label : undefined}
      style={({ isActive }) => ({
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: collapsed ? '10px 12px' : '10px 14px',
        borderRadius: 8,
        color: isActive ? 'var(--accent)' : 'var(--text-secondary)',
        backgroundColor: isActive ? 'var(--accent-soft)' : 'transparent',
        textDecoration: 'none',
        fontWeight: isActive ? 600 : 400,
        fontSize: 14,
        transition: 'background-color 0.15s, color 0.15s',
        justifyContent: collapsed ? 'center' : 'flex-start',
        minHeight: 44,
        userSelect: 'none',
      })}
      onMouseEnter={(e) => { if (!e.currentTarget.getAttribute('aria-current')) { e.currentTarget.style.backgroundColor = 'var(--accent-soft)'; e.currentTarget.style.color = 'var(--text-primary)' }}}
      onMouseLeave={(e) => { if (!e.currentTarget.getAttribute('aria-current')) { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = 'var(--text-secondary)' }}}
    >
      {icon}
      {!collapsed && (
        <>
          <span style={{ flex: 1 }}>{label}</span>
          {children}
          {pro && (
            <span style={{
              fontSize: 10, fontWeight: 700, padding: '2px 6px',
              background: 'var(--pro-gradient)',
              color: 'var(--text-on-dark)', borderRadius: 4, letterSpacing: '0.05em',
            }}>PRO</span>
          )}
          {onExpandToggle && (
            <button
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); onExpandToggle() }}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', padding: 2, display: 'flex' }}
            >
              {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            </button>
          )}
        </>
      )}
    </NavLink>
  )
}

const iconBtnStyle: React.CSSProperties = {
  background: 'none', border: 'none', cursor: 'pointer',
  color: 'var(--text-secondary)', padding: 8, borderRadius: 6,
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  minWidth: 36, minHeight: 36, flexShrink: 0,
}

const dueBadgeStyle: React.CSSProperties = {
  fontSize: 10, fontWeight: 700,
  backgroundColor: 'var(--warning)',
  color: 'var(--text-on-dark)',
  borderRadius: 50,
  padding: '1px 5px',
  lineHeight: '16px',
  minWidth: 16,
  textAlign: 'center',
}
